import { lazy, Suspense } from "react";
import { Capacitor } from "@capacitor/core";
import { useLocation, useNavigate } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AccessRetryState } from "@/components/routing/AccessRetryState";
import { RouteGateFallback } from "@/components/routing/RouteGateFallback";
import { useAuth } from "@/hooks/useAuth";
import { useCoreAccess } from "@/hooks/useCoreAccess";
import { useSubscription } from "@/hooks/useSubscription";
import { isAnonymousUser } from "@/utils/nativeAccountCompletion";

export const CORE_ACCESS_GATE_SOURCE = "core_access_gate";

// Loaded only when the gate has to show the offer, so the paywall stays out of
// the entry bundle that every page load pays for.
const PremiumPaywall = lazy(() =>
  import("@/components/composites/PremiumPaywall").then((module) => ({
    default: module.PremiumPaywall,
  })),
);


function CoreAccessPaywall() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const isGuest = isAnonymousUser(user);
  const isNativeRuntime = Capacitor.isNativePlatform();
  const returnPath = `${location.pathname}${location.search}${location.hash}`;

  return (
    <div data-jp-core-access="none" className="min-h-screen bg-jp-bg-page">
      <Suspense fallback={<RouteGateFallback />}>
        <PremiumPaywall
          open
          // Web: "Not now" goes back to Home. Native is a hard paywall (Alex,
          // Oct 3 2026: "we have generous trials if they want to try"), so the
          // offer can't be dismissed; Sign out is the only other way out.
          onOpenChange={(open) => {
            if (!open && !isNativeRuntime) navigate("/home", { replace: true });
          }}
          dismissible={!isNativeRuntime}
          type="general"
          source={CORE_ACCESS_GATE_SOURCE}
          returnPath={returnPath}
          // Native: the whole app shell sits behind this gate, so the way out is
          // signing out (restore and web sign-in live inside the paywall). A
          // guest gets Sign in instead: signing out a guest throws the guest
          // account away for good (Oct 22 2026), so the guest session stays
          // until they sign in to another account.
          alternativeAction={
            !isNativeRuntime
              ? undefined
              : isGuest
                ? {
                    label: "Sign in",
                    onClick: () => {
                      navigate(
                        `/auth?mode=signin&redirect=${encodeURIComponent(returnPath)}`,
                      );
                    },
                  }
                : {
                    label: "Sign out",
                    onClick: () => {
                      void signOut().finally(() =>
                        navigate("/", { replace: true }),
                      );
                    },
                  }
          }
        />
      </Suspense>
    </div>
  );
}

/**
 * Decides, for a signed-in member, between the screen and the Premium offer.
 * The server answer decides; a failed answer shows a retry state, never the
 * paywall, so a legacy member is never locked out by a network error. A
 * member whose store or web purchase the client already sees (before the
 * server sync lands) is let through.
 */
function CoreAccessGate({ children }: { children: React.ReactNode }) {
  const coreAccess = useCoreAccess();
  const { hasPremiumAccess, isLoadingSubscription } = useSubscription();

  if (coreAccess.status === "full" || coreAccess.status === "legacy_free") {
    return <>{children}</>;
  }
  if (hasPremiumAccess) {
    return <>{children}</>;
  }
  if (
    coreAccess.status === "loading" ||
    (coreAccess.status === "none" && isLoadingSubscription)
  ) {
    return <RouteGateFallback />;
  }
  if (coreAccess.status === "error") {
    return <AccessRetryState onRetry={() => void coreAccess.refetch()} />;
  }
  return <CoreAccessPaywall />;
}

interface CoreAccessRouteProps {
  children: React.ReactNode;
  /** Passed to ProtectedRoute. */
  skipOnboardingCheck?: boolean;
}

/**
 * Guards core surfaces that have no content-specific check (Prompt of the
 * Day, Freestyle, the native app shell). Signed-out visitors get exactly
 * ProtectedRoute's behavior. Content routes (/program/:id, /session/:id,
 * Journeys) are never wrapped: their own server checks decide.
 */
export function CoreAccessRoute({
  children,
  skipOnboardingCheck,
}: CoreAccessRouteProps) {
  return (
    <ProtectedRoute skipOnboardingCheck={skipOnboardingCheck}>
      <CoreAccessGate>{children}</CoreAccessGate>
    </ProtectedRoute>
  );
}

/**
 * Native app shell (Home, Programs, Live tabs after onboarding): a signed-in
 * native member without core access sees the offer instead of the shell.
 * On the web, and for signed-out native visitors, the route renders exactly
 * as before (the element passed in keeps its own guards).
 */
export function NativeShellCoreAccessGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();
  if (!Capacitor.isNativePlatform()) return <>{children}</>;
  if (loading) return <RouteGateFallback />;
  if (!user) return <>{children}</>;
  return <CoreAccessGate>{children}</CoreAccessGate>;
}

export default CoreAccessRoute;
